import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { Search, Users, Shield, ShieldOff, UserCog, Plus, Pencil, Loader2, Crown, Briefcase, UserCheck, FileText, Download, Upload, Trash2 } from 'lucide-react';
import { exportDataset } from '../../lib/exportData';
import { toast } from 'sonner';

const roleLabels = {
  sa: 'Super Admin',
  soa: 'Sous-Admin',
  admin: 'Admin',
  rh: 'RH',
  commercial: 'Commercial',
};

const roleBadgeStyles = {
  sa: 'bg-amber-100 text-amber-700 border-amber-200',
  soa: 'bg-violet-100 text-violet-700 border-violet-200',
  admin: 'bg-violet-100 text-violet-700 border-violet-200',
  rh: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  commercial: 'bg-slate-100 text-slate-700 border-slate-200',
};

const roleIcons = {
  sa: Crown,
  soa: Shield,
  admin: Shield,
  rh: UserCheck,
  commercial: Briefcase,
};

const DEC_DURATIONS = [
  { value: '3', label: '3 mois' },
  { value: '6', label: '6 mois' },
  { value: '8', label: '8 mois' },
  { value: '12', label: '12 mois' },
  { value: '18', label: '18 mois' },
  { value: '24', label: '24 mois' },
  { value: '36', label: '36 mois' },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SA-only inline component: seed Jean Dupont (RH) + 9 AC + optional sales reset
const SeedJeanDupontButton = () => {
  const [open, setOpen] = useState(false);
  const [resetSales, setResetSales] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runSeed = async () => {
    if (confirmText !== 'SEED') {
      toast.error('Tapez SEED pour confirmer');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await fetch(`${API}/admin/seed/jean-dupont-9ac?reset_sales=${resetSales}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setResult(data);
        toast.success('Seed termine avec succes');
        setConfirmText('');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors du seed');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setResult(null); setConfirmText(''); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="seed-jd-btn">
          Seed demo
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Seed Jean Dupont + 9 AC</DialogTitle>
          <DialogDescription>
            Cree le RH <strong>Jean Dupont</strong> (j.d@linksync.fr) et 9 AC rattaches.
            Mot de passe par defaut : <code>EduPlan2026!</code>.
            Action <strong>idempotente</strong> : les utilisateurs existants sont conserves.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                type="checkbox"
                id="reset-sales-cb"
                checked={resetSales}
                onChange={(e) => setResetSales(e.target.checked)}
                className="mt-1"
                data-testid="reset-sales-checkbox"
              />
              <Label htmlFor="reset-sales-cb" className="text-sm cursor-pointer">
                <strong>Reinitialiser toutes les ventes / commissions / factures</strong>
                <br />
                <span className="text-xs text-slate-600">
                  Supprime sales, commissions, rh_commissions, invoices, factures uploadees.
                  Les utilisateurs, FDC, attestations, documents sont conserves.
                </span>
              </Label>
            </div>
            <div className="space-y-1">
              <Label>Tapez <code>SEED</code> pour confirmer</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="SEED"
                data-testid="seed-confirm-input"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2 text-sm">
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="font-medium text-emerald-700 mb-1">Seed termine</p>
              <p className="text-xs">
                Mot de passe par defaut : <code className="bg-white px-1 rounded">{result.default_password}</code>
              </p>
            </div>
            {result.summary?.deleted && Object.keys(result.summary.deleted).length > 0 && (
              <div className="p-3 bg-rose-50 rounded-lg border border-rose-200">
                <p className="font-medium text-rose-700 mb-1">Donnees supprimees</p>
                <ul className="text-xs space-y-0.5">
                  {Object.entries(result.summary.deleted).map(([k, v]) => (
                    <li key={k}>{k}: {v}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
              <p className="font-medium text-indigo-700 mb-1">
                RH Jean Dupont ({result.summary?.rh?.status})
              </p>
              <p className="text-xs">{result.summary?.rh?.user_id}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border">
              <p className="font-medium mb-1">AC crees ({result.summary?.ac_created?.length || 0})</p>
              <ul className="text-xs space-y-0.5 max-h-32 overflow-y-auto">
                {result.summary?.ac_created?.map(ac => (
                  <li key={ac.user_id}>+ {ac.name} ({ac.email})</li>
                ))}
              </ul>
              {result.summary?.ac_skipped?.length > 0 && (
                <p className="text-xs mt-2 text-slate-500">
                  AC deja existants (ignores) : {result.summary.ac_skipped.length}
                </p>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {result ? 'Fermer' : 'Annuler'}
          </Button>
          {!result && (
            <Button
              onClick={runSeed}
              disabled={loading || confirmText !== 'SEED'}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="seed-run-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Lancer le seed
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminUsers = () => {
  const { isSA, isAdmin, isGroupSA } = useAuth();
  const [users, setUsers] = useState([]);
  const [rhUsers, setRhUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  
  // User documents (admin attachments + FDC docs)
  const [userDocuments, setUserDocuments] = useState([]);
  const [userDocsCount, setUserDocsCount] = useState(0);
  const [maxUserDocs, setMaxUserDocs] = useState(5);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docCategory, setDocCategory] = useState('Autre');
  const [docFile, setDocFile] = useState(null);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'commercial',
    agency_id: '',
    assigned_rh: '',
    dec_start_date: new Date().toISOString().split('T')[0],
    dec_duration_months: '12'
  });
  
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    agency_id: '',
    assigned_rh: '',
    dec_start_date: '',
    dec_duration_months: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchRHUsers();
    if (isGroupSA) fetchAgencies();
     
  }, [isGroupSA]);

  useEffect(() => {
    // When SA Groupe opens dialog, force role='soa' by default
    if (isCreateOpen && isGroupSA && newUser.role !== 'soa') {
      setNewUser(prev => ({ ...prev, role: 'soa' }));
    }
     
  }, [isCreateOpen, isGroupSA]);

  const fetchAgencies = async () => {
    try {
      const r = await fetch(`${API}/agencies`, { credentials: 'include' });
      if (r.ok) setAgencies(await r.json());
    } catch (e) { console.error('Failed to fetch agencies', e); }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRHUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users?role=rh`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setRhUsers(data.filter(u => u.role === 'rh'));
      }
    } catch (error) {
      console.error('Failed to fetch RH users:', error);
    }
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (newUser.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
      };
      
      // SA Groupe must provide agency_id when creating SOA
      if (isGroupSA) {
        if (!newUser.agency_id) {
          toast.error('Veuillez selectionner une filiale');
          setSubmitting(false);
          return;
        }
        payload.agency_id = newUser.agency_id;
      }
      
      // Add DEC fields for commercials
      if (newUser.role === 'commercial') {
        if (newUser.assigned_rh && newUser.assigned_rh !== 'none') payload.assigned_rh = newUser.assigned_rh;
        if (newUser.dec_start_date) payload.dec_start_date = newUser.dec_start_date;
        if (newUser.dec_duration_months) payload.dec_duration_months = parseInt(newUser.dec_duration_months);
      }
      
      const response = await fetch(`${API}/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success('Utilisateur cree avec succes');
        setIsCreateOpen(false);
        setNewUser({ name: '', email: '', password: '', role: isGroupSA ? 'soa' : 'commercial', agency_id: '', assigned_rh: '', dec_start_date: new Date().toISOString().split('T')[0], dec_duration_months: '12' });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la creation');
      }
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error('Erreur lors de la creation');
    } finally {
      setSubmitting(false);
    }
  };

  const updateUser = async () => {
    if (!editForm.name || !editForm.email) {
      toast.error('Nom et email requis');
      return;
    }
    
    if (editForm.password && editForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role
      };
      
      if (editForm.password) payload.password = editForm.password;
      
      // Only SA Groupe can change agency_id; send only if changed and provided
      if (isGroupSA && editForm.agency_id && editForm.agency_id !== selectedUser?.agency_id) {
        payload.agency_id = editForm.agency_id;
      }
      
      // Add DEC fields for commercials
      if (editForm.role === 'commercial') {
        payload.assigned_rh = (editForm.assigned_rh && editForm.assigned_rh !== 'none') ? editForm.assigned_rh : null;
        if (editForm.dec_start_date) payload.dec_start_date = editForm.dec_start_date;
        if (editForm.dec_duration_months) payload.dec_duration_months = parseInt(editForm.dec_duration_months);
      }
      
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success('Utilisateur modifie avec succes');
        setIsEditOpen(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Erreur lors de la modification');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      agency_id: user.agency_id || '',
      assigned_rh: user.assigned_rh || '',
      dec_start_date: user.dec_start_date || '',
      dec_duration_months: user.dec_duration_months?.toString() || '12'
    });
    setUserDocuments([]);
    setUserDocsCount(0);
    setDocFile(null);
    setDocCategory('Autre');
    fetchUserDocuments(user.user_id);
    setIsEditOpen(true);
  };

  const fetchUserDocuments = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/documents`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUserDocuments(data.documents || []);
        setUserDocsCount(data.user_docs_count || 0);
        setMaxUserDocs(data.max_user_docs || 5);
      } else {
        setUserDocuments([]);
      }
    } catch (error) {
      console.error('Failed to fetch user documents:', error);
      setUserDocuments([]);
    }
  };

  const uploadUserDocument = async () => {
    if (!docFile) {
      toast.error('Veuillez selectionner un fichier');
      return;
    }
    if (userDocsCount >= maxUserDocs) {
      toast.error(`Limite atteinte : ${maxUserDocs} documents maximum`);
      return;
    }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', docFile);
      fd.append('category', docCategory || 'Autre');
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}/documents`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (response.ok) {
        toast.success('Document ajoute');
        setDocFile(null);
        setDocCategory('Autre');
        fetchUserDocuments(selectedUser.user_id);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setUploadingDoc(false);
    }
  };

  const downloadUserDocument = async (docId, filename) => {
    setDownloadingDocId(docId);
    try {
      const response = await fetch(
        `${API}/admin/users/${selectedUser.user_id}/documents/${docId}/download`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Erreur de telechargement');
      }
    } catch (error) {
      toast.error('Erreur de telechargement');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const deleteUserDocument = async (docId) => {
    setDeletingDocId(docId);
    try {
      const response = await fetch(
        `${API}/admin/users/${selectedUser.user_id}/documents/${docId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (response.ok) {
        toast.success('Document supprime');
        fetchUserDocuments(selectedUser.user_id);
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setDeletingDocId(null);
    }
  };

  const getRHName = (rhId) => {
    const rh = users.find(u => u.user_id === rhId);
    return rh?.name || 'Non attribue';
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Stats by role
  const statsByRole = {
    sa: users.filter(u => u.role === 'sa').length,
    soa: users.filter(u => u.role === 'soa' || u.role === 'admin').length,
    rh: users.filter(u => u.role === 'rh').length,
    commercial: users.filter(u => u.role === 'commercial').length,
  };

  const deactivateUser = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Utilisateur desactive');
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      toast.error('Erreur lors de la desactivation');
    }
  };

  const bulkDeleteUsers = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const response = await fetch(`${API}/admin/users/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_ids: Array.from(selectedIds) }),
      });
      if (response.ok) {
        const data = await response.json();
        const del = data.deleted_count || 0;
        const skp = data.skipped_count || 0;
        if (del > 0 && skp === 0) toast.success(`${del} utilisateur(s) supprime(s)`);
        else if (del > 0 && skp > 0) toast.warning(`${del} supprime(s), ${skp} ignore(s)`);
        else toast.error(`Aucune suppression (${skp} ignore(s))`);
        setSelectedIds(new Set());
        fetchUsers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la suppression');
      }
    } catch (e) {
      toast.error('Erreur reseau');
    } finally {
      setBulkDeleting(false);
      setShowBulkConfirm(false);
    }
  };

  const toggleSelectOne = (userId, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(userId);
      else next.delete(userId);
      return next;
    });
  };

  const toggleSelectAll = (checked, list) => {
    if (checked) {
      setSelectedIds(new Set(list.filter(u => u.role !== 'sa' || isGroupSA).map(u => u.user_id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="admin-users-title">
            Gestion utilisateurs
          </h1>
          <p className="text-slate-500 mt-1">Creez et gerez les comptes utilisateurs</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* SA-only seed button */}
          {isAdmin && <SeedJeanDupontButton />}

          <Button variant="outline" onClick={() => exportDataset('users', 'utilisateurs')} data-testid="export-users-btn">
            <Download className="w-4 h-4 mr-2" /> Exporter
          </Button>
          
          {/* Create user dialog */}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Creer un utilisateur</DialogTitle>
              <DialogDescription>
                Creez un nouveau compte utilisateur avec ses identifiants
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom complet *</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Jean Dupont"
                  data-testid="new-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="jean.dupont@email.com"
                  data-testid="new-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Mot de passe * (min. 6 caracteres)</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="********"
                  data-testid="new-user-password"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                  disabled={isGroupSA}
                >
                  <SelectTrigger data-testid="new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isGroupSA ? (
                      <SelectItem value="soa">Sous-Administrateur (SOA)</SelectItem>
                    ) : (
                      <>
                        <SelectItem value="commercial">Commercial (AC)</SelectItem>
                        <SelectItem value="rh">RH (Recruteur)</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                {isGroupSA && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                    En tant que SA Groupe, vous ne pouvez creer que des Sous-Admins (SOA). Les AC et RH doivent etre crees par le SOA de la filiale.
                  </p>
                )}
              </div>

              {/* Agency selector (SA Groupe only) */}
              {isGroupSA && (
                <div className="space-y-2">
                  <Label>Filiale *</Label>
                  <Select
                    value={newUser.agency_id}
                    onValueChange={(value) => setNewUser({ ...newUser, agency_id: value })}
                  >
                    <SelectTrigger data-testid="new-user-agency">
                      <SelectValue placeholder="Selectionner une filiale" />
                    </SelectTrigger>
                    <SelectContent>
                      {agencies.filter(a => a.is_active !== false).map(a => (
                        <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* DEC fields for commercials */}
              {newUser.role === 'commercial' && (
                <>
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">Rattachement RH (DEC)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>RH de rattachement</Label>
                    <Select
                      value={newUser.assigned_rh}
                      onValueChange={(value) => setNewUser({ ...newUser, assigned_rh: value })}
                    >
                      <SelectTrigger data-testid="new-user-rh">
                        <SelectValue placeholder="Selectionner un RH" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {rhUsers.map(rh => (
                          <SelectItem key={rh.user_id} value={rh.user_id}>{rh.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date DEC (debut)</Label>
                    <Input
                      type="date"
                      value={newUser.dec_start_date}
                      onChange={(e) => setNewUser({ ...newUser, dec_start_date: e.target.value })}
                      data-testid="new-user-dec-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Duree DEC</Label>
                    <Select
                      value={newUser.dec_duration_months}
                      onValueChange={(value) => setNewUser({ ...newUser, dec_duration_months: value })}
                    >
                      <SelectTrigger data-testid="new-user-dec-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEC_DURATIONS.map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Annuler
              </Button>
              <Button onClick={createUser} disabled={submitting} data-testid="submit-create-user">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Creer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Crown className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Super Admin</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{statsByRole.sa}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-violet-100">
              <Shield className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Sous-Admins</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{statsByRole.soa}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <UserCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">RH</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{statsByRole.rh}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-slate-100">
              <Briefcase className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Commerciaux</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{statsByRole.commercial}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher un utilisateur..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-users-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]" data-testid="role-filter">
            <SelectValue placeholder="Filtrer par role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les roles</SelectItem>
            <SelectItem value="sa">Super Admin</SelectItem>
            <SelectItem value="soa">Sous-Admin</SelectItem>
            <SelectItem value="rh">RH</SelectItem>
            <SelectItem value="commercial">Commercial</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users table */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="users-table-card">
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between gap-4 p-3 bg-indigo-50 border-b border-indigo-100">
            <p className="text-sm font-medium text-indigo-900" data-testid="bulk-selection-count">
              {selectedIds.size} utilisateur(s) selectionne(s)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
                data-testid="bulk-clear-btn"
              >
                Annuler la selection
              </Button>
              <AlertDialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer la selection
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer {selectedIds.size} utilisateur(s) ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est irreversible. Les sessions actives et les acces des utilisateurs concernes seront revoques immediatement. Les utilisateurs hors de votre perimetre (autre filiale, votre propre compte) seront automatiquement ignores.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="bulk-cancel-btn">Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={bulkDeleteUsers}
                      disabled={bulkDeleting}
                      className="bg-rose-600 hover:bg-rose-700"
                      data-testid="bulk-confirm-btn"
                    >
                      {bulkDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Confirmer la suppression
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 accent-indigo-600 cursor-pointer"
                    checked={
                      filteredUsers.length > 0 &&
                      filteredUsers.filter(u => u.role !== 'sa' || isGroupSA).every(u => selectedIds.has(u.user_id))
                    }
                    onChange={(e) => toggleSelectAll(e.target.checked, filteredUsers)}
                    data-testid="bulk-select-all-checkbox"
                    aria-label="Selectionner tous les utilisateurs"
                  />
                </TableHead>
                <TableHead className="font-semibold">Utilisateur</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Role</TableHead>
                {isGroupSA && <TableHead className="font-semibold">Filiale</TableHead>}
                <TableHead className="font-semibold">Rattachement RH</TableHead>
                <TableHead className="font-semibold">Date inscription</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isGroupSA ? 8 : 7} className="text-center py-8 text-slate-500">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isGroupSA ? 8 : 7} className="text-center py-8 text-slate-500">
                    Aucun utilisateur trouve
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user, index) => {
                  const RoleIcon = roleIcons[user.role] || UserCog;
                  const canSelect = user.role !== 'sa' || isGroupSA;
                  return (
                    <TableRow 
                      key={user.user_id}
                      className="table-row-hover animate-fade-in"
                      style={{ animationDelay: `${index * 0.02}s` }}
                      data-testid={`user-row-${user.user_id}`}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-slate-300 accent-indigo-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                          checked={selectedIds.has(user.user_id)}
                          onChange={(e) => toggleSelectOne(user.user_id, e.target.checked)}
                          disabled={!canSelect}
                          title={canSelect ? '' : 'Non selectionnable'}
                          data-testid={`user-select-${user.user_id}`}
                          aria-label={`Selectionner ${user.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={user.picture} />
                            <AvatarFallback className="bg-indigo-100 text-indigo-600">
                              {user.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500">{user.email}</TableCell>
                      <TableCell>
                        <Badge className={`${roleBadgeStyles[user.role] || roleBadgeStyles.commercial} border`}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {roleLabels[user.role] || user.role}
                        </Badge>
                      </TableCell>
                      {isGroupSA && (
                        <TableCell className="text-slate-600" data-testid={`user-agency-${user.user_id}`}>
                          {user.agency_id ? (
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                              {agencies.find(a => a.agency_id === user.agency_id)?.name || user.agency_id}
                            </Badge>
                          ) : (
                            <span className="text-amber-600 text-xs font-medium">Groupe (transverse)</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-slate-500">
                        {user.role === 'commercial' && user.assigned_rh ? (
                          <span className="text-emerald-600 font-medium">{getRHName(user.assigned_rh)}</span>
                        ) : user.role === 'commercial' ? (
                          <span className="text-slate-400">Non attribue</span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(user.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(user)}
                            className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                            data-testid={`edit-user-${user.user_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          
                          {user.role !== 'sa' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-rose-500 hover:text-rose-600 hover:bg-rose-50">
                                  <ShieldOff className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Desactiver cet utilisateur ?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Cette action deconnectera l&apos;utilisateur {user.name} et supprimera ses sessions actives.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deactivateUser(user.user_id)}
                                    className="bg-rose-600 hover:bg-rose-700"
                                  >
                                    Desactiver
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Edit user dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier l&apos;utilisateur</DialogTitle>
            <DialogDescription>
              Modifiez les informations de {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom complet *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="edit-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Nouveau mot de passe (laisser vide pour ne pas changer)</Label>
              <Input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="********"
                data-testid="edit-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={editForm.role}
                onValueChange={(value) => setEditForm({ ...editForm, role: value })}
              >
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="commercial">Commercial (AC)</SelectItem>
                  <SelectItem value="rh">RH (Recruteur)</SelectItem>
                  {isSA && <SelectItem value="soa">Sous-Administrateur (SOA)</SelectItem>}
                  {isSA && selectedUser?.role === 'sa' && <SelectItem value="sa">Super Admin</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {/* Agency selector (SA Groupe only) */}
            {isGroupSA && (
              <div className="space-y-2">
                <Label>Filiale</Label>
                <Select
                  value={editForm.agency_id || 'none'}
                  onValueChange={(value) => setEditForm({ ...editForm, agency_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger data-testid="edit-user-agency">
                    <SelectValue placeholder="Aucune (SA transverse)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune (SA Groupe transverse)</SelectItem>
                    {agencies.filter(a => a.is_active !== false).map(a => (
                      <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {selectedUser?.agency_id
                    ? `Filiale actuelle : ${agencies.find(a => a.agency_id === selectedUser.agency_id)?.name || selectedUser.agency_id}`
                    : 'Utilisateur transverse (aucune filiale)'}
                </p>
              </div>
            )}
            
            {/* DEC fields for commercials */}
            {editForm.role === 'commercial' && (
              <>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">Rattachement RH (DEC)</p>
                </div>
                <div className="space-y-2">
                  <Label>RH de rattachement</Label>
                  <Select
                    value={editForm.assigned_rh}
                    onValueChange={(value) => setEditForm({ ...editForm, assigned_rh: value })}
                  >
                    <SelectTrigger data-testid="edit-user-rh">
                      <SelectValue placeholder="Selectionner un RH" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {rhUsers.map(rh => (
                        <SelectItem key={rh.user_id} value={rh.user_id}>{rh.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date DEC (debut)</Label>
                  <Input
                    type="date"
                    value={editForm.dec_start_date}
                    onChange={(e) => setEditForm({ ...editForm, dec_start_date: e.target.value })}
                    data-testid="edit-user-dec-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duree DEC</Label>
                  <Select
                    value={editForm.dec_duration_months}
                    onValueChange={(value) => setEditForm({ ...editForm, dec_duration_months: value })}
                  >
                    <SelectTrigger data-testid="edit-user-dec-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEC_DURATIONS.map(d => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* User documents section - available for ALL roles */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-indigo-600" />
                  Documents rattaches
                </p>
                <Badge variant="outline" className="bg-slate-50">
                  {userDocsCount}/{maxUserDocs} uploads admin
                </Badge>
              </div>

              {userDocuments.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-3 bg-slate-50 rounded-lg">
                  Aucun document
                </p>
              ) : (
                <div className="space-y-2 mb-4 max-h-[280px] overflow-y-auto">
                  {userDocuments.map((doc) => (
                    <div
                      key={doc.doc_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                      data-testid={`user-doc-${doc.doc_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <FileText className={`w-4 h-4 flex-shrink-0 ${doc.source === 'fdc' ? 'text-purple-500' : 'text-indigo-500'}`} />
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{doc.filename}</p>
                          <p className="text-xs text-slate-500 truncate">
                            <span className={doc.source === 'fdc' ? 'text-purple-600 font-medium' : ''}>
                              {doc.category}
                            </span>
                            {doc.uploaded_at && ` • ${new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadUserDocument(doc.doc_id, doc.filename)}
                          disabled={downloadingDocId === doc.doc_id}
                          data-testid={`download-user-doc-${doc.doc_id}`}
                        >
                          {downloadingDocId === doc.doc_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                        {doc.deletable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => deleteUserDocument(doc.doc_id)}
                            disabled={deletingDocId === doc.doc_id}
                            data-testid={`delete-user-doc-${doc.doc_id}`}
                          >
                            {deletingDocId === doc.doc_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload form */}
              {userDocsCount < maxUserDocs ? (
                <div className="space-y-2 p-3 bg-indigo-50/50 rounded-lg border border-indigo-100">
                  <Label className="text-xs font-medium text-slate-600">Ajouter un document</Label>
                  <Input
                    placeholder="Categorie (ex: Contrat, Avenant, Piece d'identite...)"
                    value={docCategory}
                    onChange={(e) => setDocCategory(e.target.value)}
                    className="h-8 text-sm"
                    data-testid="user-doc-category-input"
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                      className="h-9 text-sm cursor-pointer"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      data-testid="user-doc-file-input"
                    />
                    <Button
                      onClick={uploadUserDocument}
                      disabled={!docFile || uploadingDoc}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
                      data-testid="upload-user-doc-btn"
                    >
                      {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg text-center">
                  Limite de {maxUserDocs} documents atteinte. Supprimez un document pour en ajouter un autre.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Annuler
            </Button>
            <Button onClick={updateUser} disabled={submitting} data-testid="submit-edit-user">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
